<template>
  <div v-bind:class="{grounding: false, groundingBackground: !videoLoaded}">
    <BackgroundVideo :sources="backgroundVideoSources" :speed="0.4" @canPlayThrough="onCanPlayThrough">
      <InfiniteAudio @muted="onMute" @unmuted="onUnmute" ref="audio" :src="backgroundAudioSrc"></InfiniteAudio>

      <ExerciseWithBottomControls>
        <template v-if="!started && !finished">
        <div class="text-center">
          <div class="header dark">{{title}}</div>
          <div class="dark text bigPadding">{{description}}</div>
        </div>
          <div @click="start" class="leftRightPadding bigBottomMargin bigBottomPadding">
            <LargePrimaryButton :flat="false" :active="true" text="I am ready"></LargePrimaryButton>
          </div>

        </template>
        <template v-if="started && !finished">
          <transition name="fade">
            <div v-if="currentScenarioStage.action" class="header action bigLeftRightMargin dark text-center">
              {{currentScenarioStage.action}}
            </div>
          </transition>
          <div style="height: 4.37500rem" v-if="!currentScenarioStage.action"></div>
          <div class="imageWrapper">
            <transition name="fade">
              <div class="image" v-if="currentScenarioStage.image" :style="`background-image: url('${currentScenarioStage.image}')`"></div>
            </transition>
          </div>
          <div class="bottomSliderCaptions">
            <transition name="fade">
              <div v-if="currentScenarioStage.bottomCaption" class="sliderCaption title3 dark padding text-center">{{currentScenarioStage.bottomCaption}}</div>
            </transition>
          </div>
          <div class="safeAreaBottomMargin">
            <div class="playerControls">
              <div @click="playOrPause" v-bind:class="{small: true, dark: true, play: paused, pause: !paused}"></div>
              <div class="timer">
                <v-progress-circular width="2" size="35" :value="progressCircularValue" color="white">{{timeLeft > 0 ? timeLeft : ''}}</v-progress-circular>
              </div>
              <div @click="muteOrUnmute" v-bind:class="{small: true, dark: true, unmute: muted, mute: !muted}"></div>
            </div>
            <div class="buttonGroup text-center">
              <div @click="goBackward" v-if="canGoBackward" v-bind:class="{primaryButton: true, disabled: !canGoBackward}">
                <img class="buttonIcon" src="/icons/dark/backward.svg" alt="" />
                Return
              </div>
              <div @click="goForward" v-if="canGoForward" v-bind:class="{primaryButton: true, disabled: !canGoForward}">
                Skip
                <img class="buttonIcon" src="/icons/dark/forward.svg" alt="" />
              </div>
            </div>
          </div>
        </template>
        <template v-if="finished">
          <div class="text-center">
            <div class="header dark">Well done!</div>
            <div class="dark title3 bigPadding">You deserve to feel calm, happy, and healthy. And you have the power to do it. Please, say it to yourself.</div>
          </div>
          <div @click="close" class="leftRightPadding bigBottomMargin bigBottomPadding">
            <LargePrimaryButton :flat="false" :active="true" text="Finish exercise"></LargePrimaryButton>
          </div>
        </template>
      </ExerciseWithBottomControls>
    </BackgroundVideo>
  </div>
</template>

<script>
  import { http } from "../util/http.js"
  import moment from "moment";
  import ExerciseWithBottomControls from "@/components/containers/ExerciseWithBottomControls";
  import BackgroundVideo from "@/components/containers/BackgroundVideo";
  import InfiniteAudio from "@/components/players/InfiniteAudio";
  import LargePrimaryButton from "@/components/buttons/LargePrimaryButton";
  import {sleep} from "@/util/sleep";
  import {close} from "@/util/close"

  export default {
    components: {
      LargePrimaryButton,
      InfiniteAudio,
      BackgroundVideo,
      ExerciseWithBottomControls,
    },
    data: () => ({
      moment,
      close,
      backgroundVideoSources: [{
        src: '/exercises/grounding/backgroundVideo.mp4',
        type: 'video/mp4'
      }],
      backgroundAudioSrc: '/exercises/grounding/backgroundAudio.mp3',
      videoLoaded: false,
      title: 'Stop Anxiety',
      description: 'This exercise can be done in any situation that causes you anxiety or stress. It is important to focus your attention on your fingers. To start, sit in a comfortable position and press the button below.',
      started: false,
      audio: false,
      paused: false,
      muted: false,
      currentTime: 0,
      secondsPassed: 0,
      waitingStartedAt: 0,
      waitingFor: 0,
      startedAt: 0,
      millsPassed: 0,
      finished: false,
      showBottomCaption: true,
      showImage: true,
      showHeader: true,
      timeline: [],
      scenario: [
        {
          image: '/exercises/grounding/palmsUp.png',
          bottomSliderCaptions: [
            {
              showFor: 10000,
              text: 'Relax and let your hands rest on your lap palms up'
            }
          ],
        },
        {
          action: 'Touch your index finger to your thumb',
          image: '/exercises/grounding/indexFinger.svg',
          bottomSliderCaptions: [
            {
              text: 'As you do, go back to a time when your body felt a healthy fatigue',
              showFor: 20000,
            },
            {
              text: 'When you had just engaged in a physical activity',
              showFor: 15000
            },
            {
              text: 'Remember how good it felt to have spent all your physical energy without overdoing it',
              showFor: 11000,
            },
            {
              text: 'Your body felt heavy and your muscles were relaxing',
              showFor: 12000
            },
          ],
        },
        {
          action: 'Touch your middle finger to your thumb',
          image: '/exercises/grounding/middleFinger.svg',
          bottomSliderCaptions: [
            {
              text: 'As you do, remember what it’s like to feel a loving touch',
              showFor: 25000,
            },
            {
              text: 'It may have been a hug, or a tender arm placed around your shoulder',
              showFor: 11000
            },
            {
              text: 'Remember how good it felt to receive a supportive touch from someone important to you',
              showFor: 13000,
            }
          ],
        },
        {
          action: 'Touch your ring finger to your thumb',
          image: '/exercises/grounding/ringFinger.svg',
          bottomSliderCaptions: [
            {
              text: 'As you do, hear again the words of a sincere compliment you received',
              showFor: 30000,
            },
            {
              text: 'You made an effort, and you felt gratified that someone appreciated your contribution',
              showFor: 9000,
            }
          ],
        },
        {
          action: 'Bring down the tip of your little finger to your thumb',
          image: '/exercises/grounding/littleFinger.svg',
          bottomSliderCaptions: [
            {
              text: 'As you do, imagine looking at a beautiful outdoor view',
              showFor: 20000,
            }, {
              text: 'You felt content and inspired by the majesty of nature',
              showFor: 7000,
            }
          ],
        }
      ]

    }),

    computed: {

      currentScenarioStageIndex() {
        let currentStageIndex = 0;
        console.log(`             Run current scenario stage`);

        for (; currentStageIndex < this.timeline.length && this.millsPassed > this.timeline[currentStageIndex].showAt; currentStageIndex++) {
          console.log(`Iteration`, {
            currentStageIndex,
            millsPassed: this.millsPassed,
            showAt: this.timeline[currentStageIndex].showAt
          })
        }

        if (currentStageIndex > 0) currentStageIndex--;
        return currentStageIndex;
      },

      currentScenarioStage() {
        if (this.timeline[this.currentScenarioStageIndex].finish) this.finish();
        return this.timeline[this.currentScenarioStageIndex];
      },

      canGoForward() {
        return this.currentScenarioStageIndex < (this.timeline.length - 1);
      },

      canGoBackward() {
        return this.currentScenarioStageIndex > 3;
      },

      progressCircularValue() {
        if (this.timeLeft <= 0) return 0;

        return Math.round(((this.millsPassed - this.currentScenarioStage.showAt) / this.currentScenarioStage.duration) * 100);
      },

      timeLeft() {
        return Math.round(((this.currentScenarioStage.duration + this.currentScenarioStage.showAt) - this.millsPassed)/1000);
      }


    },

    methods: {

      playOrPause() {
        if (this.paused) this.startedAt = this.getCurrentTime() - this.millsPassed;
        this.paused = !this.paused;
      },

      async muteOrUnmute() {
        if (!this.muted) return await this.audio.mute();
        return await this.audio.unmute();
      },

      async goForward() {
        if (this.canGoForward) {
          let i = Math.min(this.currentScenarioStageIndex + 1, this.timeline.length -1)
          for (; i < this.timeline; i++) if (this.timeline[i].breakpoint) break;

          this.millsPassed = this.timeline[i].showAt + 1;
        }
      },

      goBackward() {
        if (this.canGoBackward) {
          let i = Math.max(this.currentScenarioStageIndex - 1, 0);
          for (; i > 0; i--) if (this.timeline[i].breakpoint) break;

          this.millsPassed = this.timeline[i].showAt - 1;
        }
      },

      onCanPlayThrough() {
        this.videoLoaded = true;
      },

      onMute() {
        this.muted = true;
      },

      onUnmute() {
        this.muted = false;
      },

      getCurrentTime() {
        return (new Date).getTime();
      },

      finish() {
        this.paused = true;
        this.finished = true;
        setTimeout(() => {
          this.audio.mute();
        }, 3000)
      },

      getTimeLineByScenario() {
        const showActionsAfter = 250;
        const showImagesAfter = 500;
        const showBottomCaptionsAfter = 500;

        let timeline = [{
          showAt: 0,
        }];

        let time = 0;

        for (let i = 0; i < this.scenario.length; i++) {


          if (this.scenario[i].action) {
            time += showActionsAfter;

            timeline.push({
              showAt: time,
              action: this.scenario[i].action,
            });
          }

          if (this.scenario[i].image) {

            time += showImagesAfter;

            timeline.push({
              showAt: time,
              action: this.scenario[i].action ? this.scenario[i].action : '',
              image: this.scenario[i].image,
            });

          }

          if (this.scenario[i].bottomSliderCaptions) {
            time += showBottomCaptionsAfter;

            for (let j = 0; j < this.scenario[i].bottomSliderCaptions.length; j++) {


              timeline.push({
                showAt: time,
                breakpoint: true,
                action: this.scenario[i].action,
                image: this.scenario[i].image,
                bottomCaption: this.scenario[i].bottomSliderCaptions[j].text
              });

              time += this.scenario[i].bottomSliderCaptions[j].showFor;

            }
          }
        }

        timeline.push({
          showAt: time,
          finish: true
        });

        for (let i = 1; i < timeline.length; i++) {
          timeline[i-1].duration = timeline[i].showAt - timeline[i-1].showAt;
        }

        return timeline;
      },

      async showNextBottomCaption() {
        this.showBottomCaption = false;
        this.currentScenarioSliderIndex++;
        await sleep(250);
        this.showBottomCaption = true
      },

      async showNextStage() {
        console.log(`run showing next stage`);
        this.showHeader = false;
        this.showImage = false;
        this.currentScenarioStageIndex++;
        await sleep(250);
        this.showHeader = true;
        await sleep(500);
        this.showImage = true;
      },


      async runScenario() {

        while (this.currentScenarioStageIndex < this.scenario.length) {
          let i = this.currentScenarioStageIndex;

          while (this.currentScenarioSliderIndex < this.currentScenarioStage.bottomSliderCaptions.length) {
            let j = this.currentScenarioSliderIndex;

            console.log(i, `Sleep for`, this.scenario[i].bottomSliderCaptions[j].showFor)

            this.waitingFor = Math.round(this.scenario[i].bottomSliderCaptions[j].showFor);
            this.waitingStartedAt = this.millsPassed;

            await sleep(this.scenario[i].bottomSliderCaptions[j].showFor);

            while (this.paused) {
              await sleep(1000);
            }

            await this.showNextBottomCaption();

          }

          while (this.paused) {
            await sleep(1000);
          }

          this.currentScenarioSliderIndex = 0;
          await this.showNextStage();
        }

        this.finish();


      },

      async start() {
        this.started = true;
        this.audio = this.$refs.audio;
        this.audio.play();
        this.startedAt = this.getCurrentTime();
      },

    },

    mounted() {
      this.timeline = this.getTimeLineByScenario();

      const interval = 100;
      setInterval(() => {
        if (!this.paused && this.started) {
          this.millsPassed += interval;
        }
      }, interval);
    }
  }



</script>

<style lang="scss">
  @import "src/assets/scss/index";

  .imageWrapper {
    padding: 1.25rem;
    text-align: center;
    height: 12.5rem;

    .timer {
      text-align: center;
      color: white;
      margin-top: 1rem;
      margin-left: auto;
      margin-right: auto;
    }

    .image {
      height: 10rem;
      width: 10rem;
      margin: 0 auto;
      background-size: contain;
      background-position: center;
    }
  }

  .captionControl {
    width: 70px;
    height: 70px;
    opacity: 0.5;
    cursor: pointer;

    &:active {
      opacity: 0.7 !important;
    }

    &.active {
      opacity: 1;
    }


    .controlElement {
      text-align: center;
      margin: 0 auto;
    }

    .controlName {
      text-align: center;
      color: $darkPrimaryTextColor;
      font-size: 0.9rem;
      padding-top: 6px;
    }
  }

  .action {
    font-size: 1.5rem;
    font-weight: 600;
    line-height: 2.2rem;
  }

  .bottomSliderCaptions {
    height: 7rem;


    .sliderCaption {
      height: 7rem;
    }

    .timer {
      text-align: center;
      color: white;
      margin-bottom: 1.5rem;
      margin-left: auto;
      margin-right: auto;
    }
  }

  .buttonIcon {
    width: 1.5rem;
    height: 1.5rem;
    vertical-align: bottom;
  }

  .playerControls {
    display: flex;
    justify-content: center;
    padding: 0 $bigPadding;
    align-items: center;

    & > div {
      margin: 0 1rem;
      cursor: pointer;


      &:active {
        opacity: 0.7;
      }
    }

    margin-bottom: 1.5rem;
  }

  .timer {
    color: white;
    width: 2.5rem;
    text-align: center;
  }

  .progressbar {
    margin: 0;
  }

  .play, .pause {
    width: 2.5rem !important;
    background-position: center;
  }

  .v-progress-circular, .v-progress-circular__overlay {
    transition: all 0.5s linear !important;
  }

</style>

